<template>
  <a
    class="card"
    :class="[
      `card--${backgroundColor}`,
      {
        'card--sizes-panel-open': sizesPanelIsOpen,
        'is-selected': cardIsSelected,
      },
    ]"
    :href="productUrl"
    :style="`background-color: ${hexBackgroundColor}`"
    @click="handleClick($event)"
    @mouseover="selectCard(true)"
    @mouseleave="selectCard(false)"
  >
    <div class="card__image-container" ref="imageContainer">
      <LabelBadge
        :productLabels="card.labels || []"
        :productVariantLabels="currentVariant.labels || []"
        :skus="currentVariant.skus || []"
        :isBackorderable="currentVariant.isBackorderable"
        :backgroundColor="badgeBackgroundColor"
      />
      <transition name="bg-img-fade">
        <video
          v-if="shouldShowBackground && backgroundVideoUrl"
          class="card__background-video"
          ref="backgroundVideo"
          autoplay="true"
          loop="true"
        >
          <source :src="backgroundVideoUrl" type="video/mp4" />
        </video>
        <ImageContentful
          v-else-if="shouldShowBackground"
          :src="backgroundImageUrl"
          containerSelector=".card__image-container"
          :isBackgroundImage="true"
          :options="{
            imageCompression: 81,
            retinaSupportFactor: 0.5,
            fit: CONTENTFUL_IMAGE_FIT.fill,
          }"
          class="card__background-image"
          :alt="card.name"
        />
      </transition>
      <ImageContentful
        v-show="!shouldShowBackground"
        :src="currentVariant.imageUrl"
        :lazyload="{ rootMargin: '100%' }"
        containerSelector=".card__image"
        :options="{
          imageCompression: 71,
          format: imageFormat,
          retinaSupportFactor: 0.25,
          setHeight: false,
          backgroundColor: hexBackgroundColor,
        }"
        class="card__image"
        :alt="card.name"
      />
    </div>
    <div class="card__content">
      <QuickAdd
        :variant="currentVariant"
        :onSizesPanelChange="onSizesPanelChange"
        :buttonText="$t('filter.quickadd')"
      />
      <div class="card__bottom">
        <div class="card__infos" :class="{ 'card__infos--has-background': shouldShowBackground }">
          <div class="card__title">{{ card.name }}</div>
          <div class="card__summary" v-if="!summaryIsHidden">{{ cardSummary }}</div>
          <div class="card__price">
            {{ priceWithCurrency }}
            <span class="card__separator"></span>
            {{ cardColorNames }}
          </div>
        </div>
        <ColorSelector
          :onColorSelect="onColorSelect"
          :variants="card.variants"
          :currentVariantId="currentVariant.id || 0"
          :onMouseevent="setColorSelectorHover"
          :genderToShow="genderToShow"
        />
      </div>
    </div>
  </a>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import {
  notifyExternalServices,
  ACTIONS,
} from '@/assets/javascripts/on/external-services/external-services'
import { getPathFromUrl } from '@/helpers/url.js'
import { onLoad } from '@/helpers/event'
import { format } from '@/helpers/currency-formatter'
import ImageContentful, {
  CONTENTFUL_IMAGE_FIT,
  CONTENTFUL_IMAGE_FORMAT,
} from '@/components/_shared/ImageContentful.vue'
import QuickAdd from '@/components/_shared/QuickAdd/QuickAdd.vue'
import { BACKGROUND_COLORS as BADGE_BACKGROUND_COLORS } from '@/components/_shared/Badge/Badge.vue'
import LabelBadge from '@/components/_shared/LabelBadge/LabelBadge.vue'
import ColorSelector from './ColorSelector/ColorSelector.vue'

export const CARD_COLORS = {
  light: 'light',
  ghost: 'ghost',
  dark: 'dark',
  blue: 'blue',
}

const HEX_BACKGROUND_COLORS = {
  [CARD_COLORS.light]: '#f7f7f7', // $color-grey-light-4
  [CARD_COLORS.ghost]: 'rgba(251, 251, 251, 0.6)',
  [CARD_COLORS.dark]: '#474646', // $color-grey-dark-1
  [CARD_COLORS.blue]: '#59737f',
}

export default {
  name: 'Card',
  components: { LabelBadge, ColorSelector, ImageContentful, QuickAdd },

  props: {
    backgroundColor: {
      type: String,
      validator(value) {
        return Object.values(CARD_COLORS).includes(value)
      },
      default: CARD_COLORS.light,
    },

    card: {
      type: Object,
      required: true,
    },

    context: {
      // Can be used to add context about where the card was used (e.g. 'search' - needed for GTM )
      type: String,
    },

    genderToShow: {
      type: String,
    },

    imageFormat: {
      type: String,
      default: CONTENTFUL_IMAGE_FORMAT.default,
      validator(value) {
        return Object.values(CONTENTFUL_IMAGE_FORMAT).includes(value)
      },
    },

    onCardClick: {
      type: Function,
      required: false,
      default() {
        return () => {}
      },
    },

    summaryIsHidden: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      currentVariantId: this.card.displayedVariantId,
      sizesPanelIsOpen: false,
      cardIsSelected: false,
      isHover: false,
      colorSelectorIsHovered: false,
      CONTENTFUL_IMAGE_FIT,
      imageContainerWidth: 0,
    }
  },

  computed: {
    ...mapState('store', ['currency']),
    ...mapGetters({
      vpWidth: 'viewport/width',
      isMqLarge: 'viewport/isMqLarge',
    }),

    hexBackgroundColor() {
      return HEX_BACKGROUND_COLORS[this.backgroundColor]
    },

    badgeBackgroundColor() {
      const matches = {
        [CARD_COLORS.light]: BADGE_BACKGROUND_COLORS.white,
        [CARD_COLORS.ghost]: BADGE_BACKGROUND_COLORS.white,
        [CARD_COLORS.dark]: BADGE_BACKGROUND_COLORS.grey,
        [CARD_COLORS.blue]: BADGE_BACKGROUND_COLORS.blue,
      }
      return matches[this.backgroundColor]
    },

    isMobile() {
      return !this.isMqLarge
    },

    backgroundImageUrl() {
      return this.currentVariant.backgroundImageUrl
    },

    backgroundVideoUrl() {
      return this.currentVariant.backgroundVideoUrl
    },

    shouldShowBackground() {
      return (
        this.cardIsSelected &&
        !this.colorSelectorIsHovered &&
        !this.isMobile &&
        (this.backgroundImageUrl || this.backgroundVideoUrl)
      )
    },

    isShowingAllGenders() {
      return !this.genderToShow
    },

    currentVariant() {
      const foundVariant = this.card.variants.find(variant => {
        return (
          variant.id === this.currentVariantId &&
          (this.isShowingAllGenders || variant.gender?.includes(this.genderToShow))
        )
      })
      const maybeFirstVariant = this.isShowingAllGenders
        ? this.card.variants[0]
        : this.card.variants.find(variant => variant.gender?.includes(this.genderToShow))
      return foundVariant || maybeFirstVariant || {}
    },

    productUrl() {
      let url = ''
      if (this.card.variants.length) {
        url = this.currentVariant.productUrl
        if (window.document.body.classList.contains('iframe-layout')) {
          const separator = url.indexOf('?') > -1 ? '&' : '?'
          url = url + separator + 'iframe=true'
        }
      }
      return url
    },

    // TODO: Remove once A/B test is over #DY-FILTER-PAGE-SHORT-PRODUCT-DESCRIPTION
    cardSummary() {
      const filterStore = this.$store.state.filter
      let summary = this.card.summary

      if (filterStore && filterStore.preferredCardSummary[this.card.name]) {
        const preferredSummary = filterStore.preferredCardSummary[this.card.name]

        /**
         * preferredSummary is usually a string but it can happen that two filter page item have
         * the same name. In this case, it is possible to be more specific by having a
         * default value + a value for a specific product group slug
         * @example
         *   'Weather Shirt': {
         *     default: 'Our original long-sleeve for cold-weather training.',
         *     'weather-shirt-2019': 'The next-gen training top for cold conditions.',
         *   },
         */
        summary =
          typeof preferredSummary !== 'string'
            ? preferredSummary[this.card.productGroup] || preferredSummary.default
            : preferredSummary
      }
      return summary
    },

    cardColorNames() {
      return this.card.variants.length ? this.currentVariant.colorName : []
    },

    priceWithCurrency() {
      const amount = Number(this.card.price)
      if (this.card.price !== undefined) return format(amount, this.currency)
    },
  },

  watch: {
    vpWidth: 'updateImageContainerWidth',
  },

  mounted() {
    this.updateImageContainerWidth()
    onLoad().then(this.fetchStoreData)
  },

  methods: {
    fetchStoreData() {
      this.$store.dispatch('store/fetchData')
    },

    onColorSelect(id) {
      this.currentVariantId = id
    },

    onSizesPanelChange(status) {
      this.sizesPanelIsOpen = status
    },

    handleClick(event) {
      if (this.sizesPanelIsOpen) event.preventDefault()
      this.onCardClick(this.currentVariant.spreeProduct, event)
      const isProductPageVisit = ![...event.target.classList].some(singleClass => {
        return ['sizes', 'sizes__size', 'color-selector__color'].includes(singleClass)
      })
      if (isProductPageVisit) this.trackProductListClick()
    },

    selectCard(selected) {
      this.cardIsSelected = selected
      this.$store.commit('nav/setActiveCardVariant', this.currentVariant)
    },

    setColorSelectorHover(isHover) {
      this.colorSelectorIsHovered = isHover
    },

    trackProductListClick() {
      const productData = {
        items: { id: this.currentVariant.spreeProduct.sku, price: this.card.price, position: 1 },
        currency: document.body.dataset.currency,
        list: this.context || getPathFromUrl(window.location.href),
      }

      notifyExternalServices({
        action: ACTIONS.listClick,
        productData,
      })
    },

    updateImageContainerWidth() {
      this.imageContainerWidth = this.$refs.imageContainer
        ? this.$refs.imageContainer.offsetWidth
        : 0
    },
  },
}
</script>

<style lang="scss">
@import './Card';
</style>
